import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import {
  BusinessDocumentEventRecord,
  BusinessDocumentEventTimeline,
} from './business-document-event-timeline';

interface BusinessDocumentHistoryEntry {
  event_id?: string;
  event?: string;
  event_label?: string;
  at?: string;
  actor_user_id?: string | null;
  actor_employment_id?: string | null;
  actor_name?: string | null;
  actor_email?: string | null;
  user_id?: string | null;
  document?: Record<string, unknown>;
  lines?: unknown[];
}

@Component({
  selector: 'app-business-document-events',
  standalone: false,
  templateUrl: './business-document-events.html',
  styleUrl: './business-document-events.scss',
})
export class BusinessDocumentEvents implements OnChanges, OnDestroy {
  @Input({ required: true }) documentOwner: BusinessDocumentEventRecord['document_owner'] =
    'inventory';
  @Input({ required: true }) documentType = '';
  @Input({ required: true }) documentId = '';
  @Input() title = 'Document activity';
  @Input() refreshKey = 0;
  @Input() history: unknown[] | null = [];
  @Input() view: 'all' | 'activity' | 'revisions' = 'all';

  events: BusinessDocumentEventRecord[] = [];
  historyEntries: BusinessDocumentHistoryEntry[] = [];
  loading = false;
  errorMessage = '';

  private loadTimer: ReturnType<typeof setTimeout> | null = null;
  private loadRequestId = 0;
  private timelineSubscription: Subscription | null = null;

  constructor(private readonly timeline: BusinessDocumentEventTimeline, private readonly cdr: ChangeDetectorRef) {}

  get snapshotEvents(): BusinessDocumentEventRecord[] {
    return this.events.filter(event => event.snapshot_state === 'captured' || event.snapshot_state === 'created');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['history']) {
      this.historyEntries = this.normaliseHistory(this.history);
    }

    if (
      changes['documentOwner'] ||
      changes['documentType'] ||
      changes['documentId'] ||
      changes['refreshKey']
    ) {
      this.scheduleLoad();
    }
  }

  ngOnDestroy(): void {
    if (this.loadTimer) clearTimeout(this.loadTimer);
    this.timelineSubscription?.unsubscribe();
    this.loadRequestId += 1;
  }

  actor(event: BusinessDocumentEventRecord): string {
    return event.actor_name_snapshot || event.actor_email_snapshot || event.actor_user_id || 'System';
  }

  statusTransition(event: BusinessDocumentEventRecord): string {
    if (event.from_status && event.to_status && event.from_status !== event.to_status) {
      return `${this.humanize(event.from_status)} → ${this.humanize(event.to_status)}`;
    }
    return this.humanize(event.to_status || event.from_status || '');
  }

  humanize(value: string): string {
    return value.replaceAll('_', ' ').replaceAll('-', ' ').trim();
  }

  private normaliseHistory(history: unknown[] | null): BusinessDocumentHistoryEntry[] {
    return (Array.isArray(history) ? history : [])
      .filter((entry): entry is BusinessDocumentHistoryEntry => {
        if (!entry || typeof entry !== 'object') return false;
        const revision = entry as BusinessDocumentHistoryEntry;
        return !!revision.document && Array.isArray(revision.lines);
      })
      .slice()
      .reverse();
  }

  historyActor(entry: BusinessDocumentHistoryEntry): string {
    return entry.actor_name
      || entry.actor_email
      || entry.actor_employment_id
      || entry.actor_user_id
      || entry.user_id
      || 'System';
  }

  historyLabel(entry: BusinessDocumentHistoryEntry): string {
    return entry.event_label || this.humanize(entry.event || 'Document updated');
  }

  historyStatus(entry: BusinessDocumentHistoryEntry): string {
    const document = entry.document ?? {};
    return this.humanize(String(
      document['status']
      || document['state']
      || document['current_stage']
      || document['document_stage']
      || '',
    ));
  }

  historyLineCount(entry: BusinessDocumentHistoryEntry): number {
    return Array.isArray(entry.lines) ? entry.lines.length : 0;
  }

  historyDocumentValue(entry: BusinessDocumentHistoryEntry, ...keys: string[]): string {
    const document = entry.document ?? {};
    for (const key of keys) {
      const value = document[key];
      if (value !== null && value !== undefined && String(value).trim()) return String(value);
    }
    return '—';
  }

  historyAmount(entry: BusinessDocumentHistoryEntry, ...keys: string[]): number {
    const value = this.historyDocumentValue(entry, ...keys);
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  }

  historyLines(entry: BusinessDocumentHistoryEntry): Array<Record<string, unknown>> {
    return (Array.isArray(entry.lines) ? entry.lines : [])
      .filter((line): line is Record<string, unknown> => !!line && typeof line === 'object');
  }

  historyLineValue(line: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const value = line[key];
      if (value !== null && value !== undefined && String(value).trim()) return String(value);
    }
    return '—';
  }

  historyLineAmount(line: Record<string, unknown>, ...keys: string[]): number {
    const value = this.historyLineValue(line, ...keys);
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  }

  historyCurrency(entry: BusinessDocumentHistoryEntry): string {
    const currency = this.historyDocumentValue(entry, 'currency', 'currency_code');
    return /^[A-Z]{3}$/.test(currency) ? currency : 'USD';
  }

  private scheduleLoad(): void {
    if (this.loadTimer) clearTimeout(this.loadTimer);
    const requestId = ++this.loadRequestId;
    // A cached response can resolve between Angular's normal and development
    // check-no-changes passes. Start the request on the next task so the
    // loading/template branch remains stable during the current pass.
    this.loadTimer = setTimeout(() => {
      this.loadTimer = null;
      void this.load(requestId);
    }, 0);
  }

  private load(requestId: number): void {
    this.timelineSubscription?.unsubscribe();
    this.timelineSubscription = null;

    if (!this.documentType || !this.documentId) {
      if (requestId === this.loadRequestId) {
        this.events = [];
        this.loading = false;
      }
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    // Subscribe directly so HttpClient's next/error callback commits all view
    // state before Angular starts the change-detection pass for the XHR task.
    // Converting this request to a Promise can resume between Angular's normal
    // and development-mode check-no-changes passes and trigger NG0100.
    this.timelineSubscription = this.timeline
      .list(this.documentOwner, this.documentType, this.documentId)
      .subscribe({
        next: (events) => {
          if (requestId !== this.loadRequestId) return;
          this.events = events;
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          if (requestId !== this.loadRequestId) return;
          this.events = [];
          this.errorMessage = 'Document activity could not be loaded.';
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

}
